import React, { useEffect, useMemo, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { BsArrowLeft, BsPrinter } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'

import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { resolveInstituteStampSrc } from '../utils/instituteBranding'

const formatLongDate = (value) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Pronouns for formal certificate wording when gender is known */
const getFormalPronouns = (gender) => {
  const g = String(gender || '').toLowerCase().trim()
  if (['male', 'm', 'boy'].includes(g)) {
    return { HeShe: 'He', heShe: 'he', himHer: 'him', hisHer: 'his' }
  }
  if (['female', 'f', 'girl'].includes(g)) {
    return { HeShe: 'She', heShe: 'she', himHer: 'her', hisHer: 'her' }
  }
  return { HeShe: 'He/She', heShe: 'he/she', himHer: 'him/her', hisHer: 'his/her' }
}

const sonOrDaughter = (gender) => {
  const g = String(gender || '').toLowerCase().trim()
  if (['male', 'm', 'boy'].includes(g)) return 'son'
  if (['female', 'f', 'girl'].includes(g)) return 'daughter'
  return 'son/daughter'
}

const TransferCertificate = () => {
  const { id, tcId, studentId: studentIdParam } = useParams()
  const certificateId = tcId || id
  const listBackPath = studentIdParam
    ? `/student/${studentIdParam}/transfer-certificate`
    : '/TransferCertificate'
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name: instituteContextName } = useInstitute()
  const [logoError, setLogoError] = useState(false)
  const instituteStampSrc = resolveInstituteStampSrc(
    Cookies.get('institute_stamp') || Cookies.get('stamp_url_img'),
  )

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const instituteId = Cookies.get('institute_id')
  const token = useMemo(() => {
    const userCookie = Cookies.get('user')
    try {
      return userCookie ? JSON.parse(userCookie)?.token : null
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    if (!certificateId) {
      setLoading(false)
      setError('Invalid certificate ID.')
      return
    }

    const fetchTc = async () => {
      setLoading(true)
      setError(null)
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : undefined,
          'institute-Id': instituteId ? Number(instituteId) : undefined,
        }

        const res = await axios.get(
          `${BASE_URL}/transfer-certificates/${certificateId}`,
          { headers },
        )

        const payload = res?.data?.data ?? res?.data
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          let enriched = payload
          const studentId = payload?.student_id || payload?.student?.id
          const hasPhoto =
            payload?.student?.student_image ||
            payload?.student?.image ||
            payload?.student?.photo

          // Same photo source as Admit Card (GET /student/:id) when TC payload omits image
          if (studentId && !hasPhoto) {
            try {
              const studentRes = await ApiEndpoints.viewProfile(studentId)
              const profile = studentRes?.data?.data ?? studentRes?.data
              if (profile && typeof profile === 'object') {
                enriched = {
                  ...payload,
                  student: {
                    ...(payload.student || {}),
                    student_image:
                      profile.student_image ||
                      profile.image ||
                      profile.photo ||
                      null,
                  },
                }
              }
            } catch {
              // Keep TC payload; placeholder photo will be used in the UI
            }
          }

          setData(enriched)
        } else {
          setData(null)
          setError('No certificate data returned.')
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.result ||
          err?.message ||
          'Failed to load Transfer Certificate.'
        setError(msg)
        setData(null)
        toast.error(msg)
      } finally {
        setLoading(false)
      }
    }

    fetchTc()
  }, [certificateId, instituteId, token])

  const instituteTitle =
    data?.institute?.institute_name || data?.institute?.name || instituteContextName || '—'

  const student = data?.student || {}
  const course = data?.course || {}
  const watermarkSrc = logo && !logoError ? logo : '/logo.svg'
  const studentPhotoSrc =
    student?.student_image || student?.image || student?.photo || '/studentprofile.jpg'

  const printToday = formatLongDate(new Date()) || '—'

  const courseName = course.course_name || course.name || '—'
  const pron = getFormalPronouns(student.gender)
  const childLine = sonOrDaughter(student.gender)
  const admissionDateDisplay = formatLongDate(student.admission_date)
  const completionDisplay = formatLongDate(data?.course_completion_date)

  const verificationLine =
    data?.tc_number && certificateId
      ? `Verification Ref: ${String(data.tc_number).toUpperCase()} · Record ID ${certificateId}`
      : certificateId
        ? `Record ID ${certificateId}`
        : ''

  const handlePrint = () => window.print()

  return (
    <>
      <style>
        {`
          .tc-view-page-title {
            letter-spacing: -0.02em;
            color: #223843;
            font-family: Georgia, "Times New Roman", serif;
          }
          .slc-font-serif {
            font-family: Georgia, "Times New Roman", Times, serif;
          }
          .slc-font-body {
            font-family: "Times New Roman", Times, serif;
          }

          /* A4 portrait document frame */
          .slc-doc {
            position: relative;
            width: 210mm;
            max-width: 100%;
            min-height: 297mm;
            margin: 0 auto;
            padding: 14mm 16mm 18mm;
            background: #fffffe;
            border: 2px solid #1a1a1a;
            box-sizing: border-box;
            box-shadow: 0 2px 16px rgba(0,0,0,0.06);
          }
          .slc-doc-inner {
            position: relative;
            z-index: 1;
            min-height: calc(297mm - 28mm - 36mm);
          }
          .slc-double-rule {
            border: 3px double #1a1a1a;
            padding: 10mm 12mm 12mm;
            box-sizing: border-box;
          }
          .slc-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-bottom: 12%;
            pointer-events: none;
            z-index: 0;
            overflow: hidden;
          }
          .slc-watermark img {
            width: min(72%, 340px);
            height: auto;
            max-height: 340px;
            object-fit: contain;
            opacity: 0.14;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .slc-logo-wrap {
            text-align: center;
            margin-bottom: 10px;
          }
          .slc-logo {
            width: 64px;
            height: 64px;
            object-fit: contain;
            display: inline-block;
            vertical-align: middle;
          }
          .slc-institute-name {
            font-size: 1.35rem;
            font-weight: 700;
            color: #111;
            letter-spacing: 0.02em;
            // line-height: 1.25;
            margin: 0 0 6px;
          }
          .slc-main-title {
            font-size: 1.15rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.18em;
            color: #222;
            margin: 14px 0 8px;
          }
          .slc-subtitle {
            font-size: 0.82rem;
            font-style: italic;
            color: #444;
            max-width: 42em;
            margin: 0 auto 18px;
            // line-height: 1.45;
          }

          .slc-rule {
            border: none;
            // border-top: 1px solid #333;
            margin: 14px 0 16px;
          }

          .slc-particulars-block {
            margin-bottom: 0;
          }
          .slc-particulars-block > .slc-section-h {
            margin-top: 0;
            margin-bottom: 0;
          }
          .slc-particulars-row {
            display: flex;
            align-items: stretch;
            gap: 14px;
            margin: 0;
            padding: 8px 0 10px;
          }
          .slc-particulars-details {
            flex: 1 1 auto;
            min-width: 0;
            display: flex;
            flex-direction: column;
          }
          .slc-student-photo-wrap {
            flex: 0 0 118px;
            width: 118px;
            align-self: stretch;
            position: relative;
            padding: 0;
            min-height: 112px;
          }
          .slc-student-photo {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            border: 1px solid #111827;
            border-radius: 4px;
            background: #f8fafc;
            display: block;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .slc-details-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.92rem;
            margin-bottom: 0;
            height: 100%;
          }
          .slc-details-table td {
            padding: 5px 8px 5px 0;
            vertical-align: top;
            // border-bottom: 1px solid #e0e0e0;
          }
          .slc-details-table td:first-child {
            width: 38%;
            font-weight: 600;
            color: #333;
          }

          .slc-section-h {
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            color: #333;
            margin: 18px 0 8px;
            padding-bottom: 4px;
            // border-bottom: 1px solid #ccc;
          }
          .slc-particulars-block + .slc-section-h {
            margin-top: 10px;
          }

          .slc-prose {
            font-size: 0.95rem;
            // line-height: 1.65;
            text-align: justify;
            color: #1a1a1a;
            margin: 16px 0 12px;
            text-indent: 1.5em;
          }
          .slc-prose:first-of-type {
            text-indent: 0;
          }
          .slc-prose strong {
            font-weight: 700;
          }

          .slc-meta-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px 20px;
            font-size: 0.88rem;
            margin-top: 14px;
          }
          .slc-meta-item {
            display: flex;
            gap: 6px;
            // border-bottom: 1px solid #e0e0e0;
            padding-bottom: 4px;
          }
          .slc-meta-label {
            font-weight: 600;
            color: #333;
            white-space: nowrap;
          }

          .slc-footer {
            margin-top: 28px;
            padding-top: 12px;
            border-top: 1px solid #333;
          }
          .slc-stamp {
            width: 72px;
            height: 72px;
            border: 2px solid #999;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.55rem;
            color: #888;
            text-align: center;
            // line-height: 1.2;
            padding: 6px;
            flex-shrink: 0;
            object-fit: contain;
            overflow: hidden;
          }
          .slc-verify {
            font-size: 0.68rem;
            color: #666;
            letter-spacing: 0.04em;
            margin-top: 14px;
            font-family: ui-monospace, monospace;
          }

          @media screen and (max-width: 768px) {
            .slc-doc {
              min-height: auto;
              padding: 12mm 10mm;
            }
            .slc-meta-grid {
              grid-template-columns: 1fr;
            }
            .slc-particulars-row {
              gap: 10px;
            }
            .slc-student-photo-wrap {
              flex-basis: 96px;
              width: 96px;
            }
          }

          @media print {
            .tc-view-no-print {
              display: none !important;
            }

            /* App layout: hide sidebar + mobile top bar; only main content column prints */
            .App {
              display: block !important;
            }
            .App > *:not(:last-child) {
              display: none !important;
            }
            .App > div:last-child {
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
            }

            /* Explicit A4 portrait (210 × 297 mm) — width < height */
            @page {
              size: 210mm 297mm;
              margin: 6mm 8mm;
            }
            html {
              print-color-adjust: exact;
            }
            body {
              background: #fff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .tc-print-root {
              margin: 0 !important;
              padding: 0 !important;
            }
            .tc-print-root .container-fluid {
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-bottom: 0 !important;
              max-width: 100% !important;
            }

            /* Fill printable height: A4 297mm − @page margins (6mm+6mm) = 285mm */
            .slc-doc {
              box-shadow: none !important;
              width: 100% !important;
              max-width: 100% !important;
              min-height: 285mm !important;
              display: flex !important;
              flex-direction: column !important;
              padding: 5mm 6mm 5mm !important;
              border: none !important;
              page-break-after: avoid;
              box-sizing: border-box !important;
              // line-height: 3.2 !important;
            }
            .slc-doc-inner {
              flex: 1 1 auto !important;
              display: flex !important;
              flex-direction: column !important;
              min-height: 0 !important;
            }
            .slc-double-rule {
              border: 3px double #000 !important;
              padding: 4mm 5mm 4mm !important;
            }
            .slc-watermark {
              padding-bottom: 8% !important;
            }
            .slc-watermark img {
              width: min(78%, 380px) !important;
              max-height: 380px !important;
              opacity: 0.12 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .slc-logo-wrap {
              margin-bottom: 4px !important;
            }
            .slc-logo {
              width: 44px !important;
              height: 44px !important;
            }
            .slc-institute-name {
              font-size: 1.05rem !important;
              margin: 0 0 2px !important;
            }
            .slc-main-title {
              font-size: 0.78rem !important;
              letter-spacing: 0.12em !important;
              margin: 4px 0 2px !important;
            }
            .slc-subtitle {
              font-size: 0.7rem !important;
              margin: 0 auto 6px !important;
              // line-height: 1.3 !important;
            }
            .slc-rule {
              margin: 6px 0 8px !important;
            }
            .slc-section-h {
              font-size: 0.62rem !important;
              margin: 8px 0 3px !important;
              padding-bottom: 2px !important;
            }
            .slc-details-table {
              font-size: 0.78rem !important;
              margin-bottom: 2px !important;
            }
            .slc-details-table td {
              padding: 2px 4px 2px 0 !important;
            }
            .slc-particulars-block {
              page-break-inside: avoid;
            }
            .slc-particulars-row {
              gap: 10px !important;
              padding: 4px 0 6px !important;
              align-items: stretch !important;
              page-break-inside: avoid;
            }
            .slc-student-photo-wrap {
              flex-basis: 90px !important;
              width: 90px !important;
              padding: 0 !important;
              align-self: stretch !important;
              position: relative !important;
              min-height: 88px !important;
            }
            .slc-student-photo {
              position: absolute !important;
              inset: 0 !important;
              width: 100% !important;
              height: 100% !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .slc-particulars-block + .slc-section-h {
              margin-top: 6px !important;
            }
            .slc-meta-grid {
              gap: 2px 12px !important;
              margin-top: 4px !important;
              font-size: 0.75rem !important;
            }
            .slc-meta-item {
              padding-bottom: 1px !important;
            }
            .slc-prose {
              font-size: 0.82rem !important;
              // line-height: 1.38 !important;
              margin: 6px 0 4px !important;
            }
            .slc-footer {
              margin-top: auto !important;
              padding-top: 6px !important;
            }
            .slc-stamp {
              width: 52px !important;
              height: 52px !important;
              font-size: 0.48rem !important;
            }
            .slc-verify {
              margin-top: 6px !important;
              font-size: 0.58rem !important;
            }
          }
        `}
      </style>

      <div
        className="tc-print-root"
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
          paddingBottom: '48px',
        }}
      >
        <div
          className="tc-view-no-print"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            marginLeft: isMobile ? '-13px' : '-40px',
            marginRight: isMobile ? '-13px' : '-40px',
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid #D1D3D4',
            }}
          >
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {!logoError ? (
                  <img
                    src={logo}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              <div>
                <h3 className="m-0 fw-bold tc-view-page-title text-start">Transfer Certificate</h3>
              </div>
            </div>
            {!isMobile && <ProfileDropdown logout={logout} />}
          </div>
        </div>

        <div
          className="tc-view-no-print container-fluid px-3 px-md-4 mt-3 mb-3"
          style={{ maxWidth: 920 }}
        >
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end w-100">
            <Button
              variant="outline-secondary"
              className="d-flex align-items-center gap-2"
              style={{ borderRadius: '10px', borderColor: '#D1D3D4' }}
              onClick={() => navigate(listBackPath)}
            >
              <BsArrowLeft /> Back to list
            </Button>
            <Button
              style={{
                borderRadius: '10px',
                backgroundColor: '#223843',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onClick={handlePrint}
              disabled={!data || loading}
            >
              <BsPrinter size={18} /> Print
            </Button>
          </div>
        </div>

        <div className="container-fluid px-2 px-md-4 pb-4" style={{ maxWidth: 920 }}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center py-5 gap-2 text-secondary">
              <Spinner animation="border" size="sm" />
              <span className="slc-font-body">Loading certificate…</span>
            </div>
          )}

          {!loading && error && !data && (
            <div
              className="text-center py-5 px-3 rounded"
              style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#991B1B',
                fontFamily: 'Georgia, serif',
              }}
            >
              {error}
            </div>
          )}

          {!loading && data && (
            <div className="slc-doc slc-font-body">
              <div className="slc-watermark" aria-hidden="true">
                <img
                  src={watermarkSrc}
                  alt=""
                  onError={() => setLogoError(true)}
                />
              </div>
              <div className="slc-doc-inner">
                <header>
                  <div className="slc-logo-wrap">
                    {!logoError ? (
                      <img
                        className="slc-logo"
                        src={watermarkSrc}
                        alt=""
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={40} className="text-dark slc-logo" />
                    )}
                  </div>
                  <h1 className="slc-institute-name slc-font-serif text-center">{instituteTitle}</h1>
                  <h2 className="slc-main-title slc-font-serif text-center">
                    Institute Leaving Certificate
                  </h2>
                  <p className="slc-subtitle slc-font-body text-center">
                    ( Transfer Certificate )
                  </p>
                  <p className="slc-subtitle slc-font-body text-center" style={{ marginTop: '-4px' }}>
                    This is to certify that the following student has left the institution.
                  </p>
                  <hr className="slc-rule" />
                </header>

                <div className="slc-particulars-block">
                  <div className="slc-section-h" style={{ borderBottom: '1px solid #ccc' }}>
                    Particulars of the student
                  </div>
                  <div className="slc-particulars-row">
                    <div className="slc-particulars-details">
                      <table className="slc-details-table">
                        <tbody>
                          <tr className="text-start">
                            <td>Student name</td>
                            <td>{student.name || '—'}</td>
                          </tr>
                          <tr className="text-start">
                            <td>Father&apos;s name</td>
                            <td>{student.father_name || '—'}</td>
                          </tr>
                          <tr className="text-start">
                            <td>Mother&apos;s name</td>
                            <td>{student.mother_name || '—'}</td>
                          </tr>
                          <tr className="text-start">
                            <td>Admission number</td>
                            <td>{student.admission_number || '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="slc-student-photo-wrap">
                      <img
                        className="slc-student-photo"
                        src={studentPhotoSrc}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.onerror = null
                          e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="slc-section-h" style={{ borderBottom: '1px solid #ccc' }}>Academic details</div>
                <div className="slc-meta-grid">
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Course / Class</span>
                    <span>{courseName}</span>
                  </div>
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Duration</span>
                    <span>
                      {course.course_duration != null && course.course_duration !== ''
                        ? `${course.course_duration} months`
                        : '—'}
                    </span>
                  </div>
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Date of admission</span>
                    <span>{admissionDateDisplay || '—'}</span>
                  </div>
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Date of leaving / completion</span>
                    <span>{completionDisplay || '—'}</span>
                  </div>
                </div>

                <div className="slc-section-h" style={{ borderBottom: '1px solid #ccc' }}>Certificate particulars</div>
                <div className="slc-meta-grid">
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Certificate No.</span>
                    <span>{data.tc_number || '—'}</span>
                  </div>
                  <div className="slc-meta-item">
                    <span className="slc-meta-label">Date of issue</span>
                    <span>{formatDateTime(data.tc_issue_date)}</span>
                  </div>
                  <div className="slc-meta-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="slc-meta-label">Reason for leaving</span>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{data.reason || '—'}</span>
                  </div>
                  <div className="slc-meta-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="slc-meta-label">Remarks</span>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{data.remarks || '—'}</span>
                  </div>
                </div>

                <div className="slc-section-h" style={{ marginTop: '22px', borderBottom: '1px solid #ccc' }}>
                  Declaration
                </div>
                <p className="slc-prose">
                  This is to certify that <strong>{student.name || '—'}</strong>, {childLine} of{' '}
                  <strong>{student.father_name || '—'}</strong>, was a bonafide student of this institution.{' '}
                  {pron.HeShe} has successfully completed the course <strong>{courseName}</strong> and is
                  hereby issued this Institute Leaving Certificate.
                </p>
                <p className="slc-prose">
                  We wish {pron.himHer} success in future endeavours.
                </p>

                <footer className="slc-footer">
                  <div className="d-flex flex-wrap justify-content-between align-items-end gap-4">
                    <div style={{ minWidth: '140px' }}>
                      <div style={{ fontSize: '0.88rem' }}>
                        <strong>Date:</strong> {printToday}
                      </div>
                    </div>
                    <div className="d-flex align-items-end gap-3 flex-wrap">
                      <img
                        className="slc-stamp"
                        src={instituteStampSrc}
                        alt="Institute stamp"
                      />
                      <div className="text-end" style={{ minWidth: '200px' }}>
                        <div
                          style={{
                            minHeight: '44px',
                            borderBottom: '1px solid #111',
                            marginBottom: '6px',
                          }}
                        />
                        <div className="slc-font-serif" style={{ fontSize: '0.9rem', fontWeight: 700 }}>
                          Authorized Signatory
                        </div>
                        <div style={{ fontSize: '0.82rem', color: '#444', marginTop: '2px' }}>
                          {instituteTitle}
                        </div>
                      </div>
                    </div>
                  </div>
                  {verificationLine && (
                    <div className="slc-verify text-center text-md-start">{verificationLine}</div>
                  )}
                </footer>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default TransferCertificate
